import { useEffect, useRef, useState } from 'react'
import styles from './WhatThisIsnt.module.css'

// Each principle is a commitment + a quiet elaboration
const denials = [
  {
    statement: 'It knows when to stay quiet.',
    elaboration: 'Autocomplete fills space. Box Editor earns it, and sometimes the most useful thing it can do is nothing.',
  },
  {
    statement: 'It works at your pace, not ahead of it.',
    elaboration: 'Speed that outstrips understanding creates a different kind of debt. Box Editor moves with you, not in front of you.',
  },
  {
    statement: 'It is careful where care is required.',
    elaboration: 'Confidence is easy to fake. The harder thing, and the thing worth building, is knowing where to slow down.',
  },
  {
    statement: 'It works within your system, not over it.',
    elaboration: 'Box Editor learns your codebase as it is, not as a generic template. It works with your decisions, not around them.',
  },
  {
    statement: 'It is optimized for right, not impressive.',
    elaboration: 'A suggestion that looks smart but is wrong costs more than no suggestion at all. Right is the only bar that matters.',
  },
]

const WhatThisIsnt = () => {
  const sectionRef = useRef<HTMLElement>(null)
  const lineRefs = useRef<(HTMLDivElement | null)[]>([])
  const [visibleLines, setVisibleLines] = useState<boolean[]>(
    new Array(denials.length).fill(false)
  )
  const [scrollProgress, setScrollProgress] = useState(0)
  const [headerVisible, setHeaderVisible] = useState(false)

  useEffect(() => {
    const observers: IntersectionObserver[] = []

    // Header observer
    const headerObserver = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setHeaderVisible(true) },
      { threshold: 0.3 }
    )
    if (sectionRef.current) headerObserver.observe(sectionRef.current)
    observers.push(headerObserver)

    // Per-line observers — stagger reveal as each enters viewport
    lineRefs.current.forEach((el, i) => {
      if (!el) return
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleLines(prev => {
              const next = [...prev]
              next[i] = true
              return next
            })
          }
        },
        { threshold: 0.25 }
      )
      obs.observe(el)
      observers.push(obs)
    })

    // Scroll for ambient parallax
    const handleScroll = () => {
      if (!sectionRef.current) return
      const rect = sectionRef.current.getBoundingClientRect()
      const progress = Math.max(0, Math.min(1,
        (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
      ))
      setScrollProgress(progress)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      observers.forEach(o => o.disconnect())
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <section ref={sectionRef} className={styles.section}>

      {/* Ambient background — diagonal scan texture */}
      <div className={styles.scanTexture} />

      {/* Drifting glow orb */}
      <div
        className={styles.orb}
        style={{ transform: `translate(-50%, calc(-50% + ${scrollProgress * 60}px))` }}
      />

      <div className="container">
        <div className={styles.layout}>

          {/* Sticky label column */}
          <div className={styles.labelColumn}>
            <div
              className={styles.sectionTag}
              style={{
                opacity: headerVisible ? 1 : 0,
                transform: headerVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: 'opacity 0.8s ease, transform 0.8s ease',
              }}
            >
              <span className={styles.tagNumber}>04</span>
              <span className={styles.tagLine} />
              <span className={styles.tagText}>What We Believe</span>
            </div>
          </div>

          {/* Content column */}
          <div className={styles.contentColumn}>

            {/* Preamble */}
            <p
              className={styles.preamble}
              style={{
                opacity: headerVisible ? 1 : 0,
                transform: headerVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.9s ease 0.15s, transform 0.9s ease 0.15s',
              }}
            >
              Every decision in Box Editor comes back to one question: does this actually
              help the person writing the code?
            </p>

            {/* Denial rows */}
            <div className={styles.denialList}>
              {denials.map((denial, i) => (
                <div
                  key={i}
                  ref={el => lineRefs.current[i] = el}
                  className={styles.denialRow}
                  style={{
                    opacity: visibleLines[i] ? 1 : 0,
                    transform: visibleLines[i] ? 'translateX(0)' : 'translateX(-24px)',
                    transition: `opacity 0.7s ease ${i * 0.04}s, transform 0.7s ease ${i * 0.04}s`,
                  }}
                >
                  {/* Strike-bar that animates in */}
                  <div
                    className={styles.strikeBar}
                    style={{
                      width: visibleLines[i] ? '100%' : '0%',
                      transition: `width 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${0.25 + i * 0.05}s`,
                    }}
                  />

                  <div className={styles.denialInner}>
                    <p className={styles.denialStatement}>{denial.statement}</p>
                    <p className={styles.denialElaboration}>{denial.elaboration}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Closing line */}
            <p
              className={styles.closing}
              style={{
                opacity: visibleLines[denials.length - 1] ? 1 : 0,
                transform: visibleLines[denials.length - 1] ? 'translateY(0)' : 'translateY(16px)',
                transition: 'opacity 0.9s ease 0.3s, transform 0.9s ease 0.3s',
              }}
            >
              Careful, precise, and genuinely useful. Not because those are good
              marketing words, but because{' '}
              <em className={styles.closingEmphasis}>wrong</em>{' '}
              is expensive and we've felt that cost ourselves.
            </p>

          </div>
        </div>
      </div>
    </section>
  )
}

export default WhatThisIsnt
